import { useTable } from "@refinedev/core";
import { Link, useSearchParams } from "react-router";

import { Badge } from "@/components/Badge";
import {
  ErrorBox,
  FilterBar,
  Loading,
  PageHeader,
  Pagination,
  SearchInput,
  Select,
} from "@/components/Page";
import { EmptyRow, Sub, Table, Td, Th, Tr } from "@/components/Table";
import {
  MEMOIR_STATUS_OPTIONS,
  PLAN_OPTIONS,
  formatDateTime,
  memoirStatus,
  planLabel,
} from "@/lib/format";
import { filterValue } from "@/lib/query";

export interface MemoirRow extends Record<string, unknown> {
  id: string;
  title: string;
  status: string;
  plan: string;
  storyteller_name: string | null;
  prompts_sent: number;
  answers: number;
  response_rate: number | null;
  stories_count: number;
  photos_count: number;
  recordings_count: number;
  last_activity_at: string | null;
  paused: boolean;
  family_completion: boolean;
}

export function MemoirsListPage() {
  // Lista rodzin linkuje tutaj z gotowym filtrem, więc rodzina z adresu
  // jest stanem początkowym tabeli.
  const [searchParams] = useSearchParams();
  const rodzinaZAdresu = searchParams.get("family_id") ?? "";

  const { result, tableQuery, filters, setFilters, currentPage, setCurrentPage, pageSize } =
    useTable<MemoirRow>({
      resource: "memoirs",
      sorters: { initial: [{ field: "created_at", order: "desc" }] },
      filters: {
        initial: rodzinaZAdresu
          ? [{ field: "family_id", operator: "eq", value: rodzinaZAdresu }]
          : undefined,
      },
    });

  const ustawFiltr = (field: string, value: string) =>
    setFilters([{ field, operator: "eq", value }], "merge");

  return (
    <>
      <PageHeader
        title="Opowieści"
        description="Czy Opowieść żyje. Status mówi mniej niż odsetek odpowiedzi i data ostatniej aktywności - Opowieść aktywna, w której od miesiąca nic się nie dzieje, wygląda tu inaczej niż w tabeli statusów."
      />

      <FilterBar>
        <Select
          label="Status"
          value={filterValue(filters, "status")}
          options={MEMOIR_STATUS_OPTIONS}
          onChange={(value) => ustawFiltr("status", value)}
        />
        <Select
          label="Pakiet"
          value={filterValue(filters, "plan")}
          options={PLAN_OPTIONS}
          onChange={(value) => ustawFiltr("plan", value)}
        />
        <Select
          label="Harmonogram"
          value={filterValue(filters, "paused")}
          emptyLabel="Wszystkie"
          options={[
            { value: "true", label: "Wstrzymany" },
            { value: "false", label: "Chodzi" },
          ]}
          onChange={(value) => ustawFiltr("paused", value)}
        />
        <SearchInput
          label="Szukaj"
          placeholder="tytuł, imię lub adres narratora"
          value={filterValue(filters, "query")}
          onChange={(value) => ustawFiltr("query", value)}
        />
      </FilterBar>

      <ErrorBox error={tableQuery.error} />

      {tableQuery.isLoading ? (
        <Loading />
      ) : (
        <>
          <Table>
            <thead>
              <tr>
                <Th>Opowieść</Th>
                <Th>Narrator</Th>
                <Th>Status</Th>
                <Th align="right">Odsetek odpowiedzi</Th>
                <Th align="right">Historie</Th>
                <Th align="right">Zdjęcia</Th>
                <Th align="right">Nagrania</Th>
                <Th>Ostatnia aktywność</Th>
              </tr>
            </thead>
            <tbody>
              {result.data.length === 0 && (
                <EmptyRow colSpan={8}>Żadna Opowieść nie pasuje do tych filtrów.</EmptyRow>
              )}
              {result.data.map((memoir) => {
                const status = memoirStatus(memoir.status);
                return (
                  <Tr key={memoir.id}>
                    <Td>
                      <Link
                        to={`/opowiesci/${memoir.id}`}
                        className="text-accent underline underline-offset-4 hover:text-accent-dark"
                      >
                        {memoir.title}
                      </Link>
                      <Sub>
                        {planLabel(memoir.plan)}
                        {memoir.family_completion && " - dokańcza rodzina"}
                        {memoir.paused && " - pytania wstrzymane"}
                      </Sub>
                    </Td>
                    <Td nowrap>{memoir.storyteller_name ?? "-"}</Td>
                    <Td>
                      <Badge tone={status.tone}>{status.label}</Badge>
                    </Td>
                    <Td align="right" numeric nowrap>
                      {memoir.response_rate === null ? (
                        // Brak wysyłek to brak danych, nie zero procent.
                        <span className="text-ink-soft">jeszcze nie wiadomo</span>
                      ) : (
                        `${memoir.response_rate}% (${memoir.answers}/${memoir.prompts_sent})`
                      )}
                    </Td>
                    <Td align="right" numeric>
                      {memoir.stories_count}
                    </Td>
                    <Td align="right" numeric>
                      {memoir.photos_count}
                    </Td>
                    <Td align="right" numeric>
                      {memoir.recordings_count}
                    </Td>
                    <Td nowrap>{formatDateTime(memoir.last_activity_at)}</Td>
                  </Tr>
                );
              })}
            </tbody>
          </Table>

          <Pagination
            page={currentPage}
            pageSize={pageSize}
            total={result.total ?? 0}
            onChange={setCurrentPage}
          />
        </>
      )}
    </>
  );
}
